/**
 * Version Check Utility
 * Detects new deployments and auto-refreshes the app
 * Also provides a manual hard-refresh mechanism
 */
import { isRunningInWebView, webViewHardRefresh, initWebViewCacheStrategy } from './webviewCache';

const VERSION_KEY = 'app_version';
const VERSION_CHECK_INTERVAL = 30000; // Check every 30 seconds

export const initVersionCheck = () => {
  // Initialize WebView cache strategy if needed
  initWebViewCacheStrategy();

  // Get current build time from HTML meta tag or generate one
  const getCurrentVersion = () => {
    const meta = document.querySelector('meta[name="build-timestamp"]');
    if (meta) return meta.content;

    // Fallback: use current timestamp
    return new Date().getTime().toString();
  };

  const storedVersion = localStorage.getItem(VERSION_KEY);
  const currentVersion = getCurrentVersion();

  // If first load, store version
  if (!storedVersion) {
    localStorage.setItem(VERSION_KEY, currentVersion);
    return;
  }

  // If version changed, hard refresh
  if (storedVersion !== currentVersion) {
    console.log('New version detected. Hard refreshing...');
    hardRefresh();
  }

  // Periodically check for version updates (useful for long-running sessions)
  setInterval(() => {
    const latestVersion = getCurrentVersion();
    const savedVersion = localStorage.getItem(VERSION_KEY);

    if (latestVersion && savedVersion && latestVersion !== savedVersion) {
      console.log('New deployment detected. Hard refreshing...');
      hardRefresh();
    }
  }, VERSION_CHECK_INTERVAL);
};

export const hardRefresh = () => {
  // Use WebView-specific refresh if running in WebView
  if (isRunningInWebView()) {
    webViewHardRefresh();
    return;
  }

  // Clear service workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => registration.unregister());
    });
  }

  // Clear all caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }

  // Clear IndexedDB
  if ('indexedDB' in window) {
    const dbs = ['travaio', 'app-cache'];
    dbs.forEach(dbName => {
      try {
        const request = indexedDB.deleteDatabase(dbName);
        request.onerror = () => console.log(`Could not delete ${dbName}`);
      } catch (e) {
        console.log(`Could not access ${dbName}`);
      }
    });
  }

  // Preserve auth token only
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const userInfo = localStorage.getItem('userInfo');

  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();

  // Restore essential auth data
  if (token) localStorage.setItem('token', token);
  if (userId) localStorage.setItem('userId', userId);
  if (userInfo) localStorage.setItem('userInfo', userInfo);

  // Remove any reload blockers
  sessionStorage.removeItem('chunk_reload');

  // Navigate with a cache-busting param so the request can't be served from
  // browser/CDN HTTP cache - clearing Cache Storage above doesn't affect that.
  const url = new URL(window.location.origin + window.location.pathname + window.location.search);
  url.searchParams.set('_hr', Date.now());
  window.location.href = url.toString();
};

export const manualHardRefresh = () => {
  const confirmed = window.confirm(
    'This will clear the app cache and refresh. You will remain logged in.\n\nContinue?'
  );
  if (confirmed) {
    hardRefresh();
  }
};

// Global function for manual trigger from console
window.__travaioHardRefresh = manualHardRefresh;
